import {getLocationAvailabilityAPI} from "../network/publicCalls/GETLocationAvailability";

const pad = (value) => String(value).padStart(2, "0");

export const formatAvailabilityDateTimeNl = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * One card per group of equivalent vehicles (`equivalence_key` from the vehicle search): the backend marks the
 * vehicle to open first with `is_preferred`. Vehicles without a key (older API) stay as they are.
 */
export const collapseEquivalentVehicles = (vehicles) => {
    if (!Array.isArray(vehicles)) return [];

    const chosenByKey = new Map();
    vehicles.forEach((vehicle) => {
        const key = vehicle?.equivalence_key;
        if (!key) return;
        const current = chosenByKey.get(key);
        if (!current || (!current.is_preferred && vehicle.is_preferred)) {
            chosenByKey.set(key, vehicle);
        }
    });

    return vehicles.filter((vehicle) => (
        !vehicle?.equivalence_key || chosenByKey.get(vehicle.equivalence_key) === vehicle
    ));
};

/**
 * Which vehicle to book for the period: the requested one when it is free, otherwise a free equivalent vehicle on
 * the location (`suggested_vehicle_id`).
 *
 * - `free`: book the requested vehicle;
 * - `swapped`: book `vehicleId` instead;
 * - `unavailable`: no equivalent vehicle is free, `vehicles` lists what blocks each of them;
 * - `unknown`: the check could not run (older API, network). Keep the single-vehicle behaviour; the backend still
 *   refuses a double booking.
 */
export const resolveBookableVehicle = async ({locationId, vehicleId, startDate, endDate}) => {
    if (!locationId || !vehicleId || !startDate || !endDate) return {status: "unknown", vehicles: []};

    let response;
    try {
        response = await getLocationAvailabilityAPI({locationId, vehicleId, startDate, endDate});
    } catch (error) {
        return {status: "unknown", vehicles: []};
    }

    const vehicles = Array.isArray(response?.vehicles) ? response.vehicles : [];
    const suggestedVehicleId = response?.suggested_vehicle_id;

    if (response?.can_rent && suggestedVehicleId) {
        return {
            status: String(suggestedVehicleId) === String(vehicleId) ? "free" : "swapped",
            vehicleId: String(suggestedVehicleId),
            vehicles,
        };
    }

    return {status: "unavailable", vehicles};
};

const defaultFormatRange = (start, end) => {
    const from = formatAvailabilityDateTimeNl(start);
    const to = formatAvailabilityDateTimeNl(end);
    return from && to ? `bezet van ${from} tot ${to}` : "bezet";
};

/**
 * One line per reservation that blocks the period ("V25HZT — bezet van … tot …"). `withPlate: false` drops the
 * plate, for a location with a single vehicle.
 */
export const buildOccupancyLines = (vehicles, {formatRange = defaultFormatRange, withPlate = true, separator = " — "} = {}) => (
    (Array.isArray(vehicles) ? vehicles : []).reduce((lines, vehicle) => {
        if (!vehicle || vehicle.can_rent) return lines;

        const prefix = withPlate && vehicle.license_plate ? `${vehicle.license_plate}${separator}` : "";
        const withPrefix = (text) => (prefix ? `${prefix}${text}` : text.charAt(0).toUpperCase() + text.slice(1));
        const ranges = Array.isArray(vehicle.occupied_at) ? vehicle.occupied_at : [];

        if (ranges.length === 0) {
            return lines.concat(withPrefix("niet beschikbaar"));
        }

        return lines.concat(ranges.map((range) => withPrefix(formatRange(range?.start_date, range?.end_date))));
    }, [])
);

/** Text for "no equivalent vehicle is free": intro, one line per blocking reservation, outro. */
export const buildUnavailableMessage = (vehicles) => {
    const list = Array.isArray(vehicles) ? vehicles : [];
    const hasEquivalents = list.length > 1;

    return {
        title: hasEquivalents ? "Geen voertuig beschikbaar" : "Voertuig niet beschikbaar",
        intro: hasEquivalents
            ? "Op de gekozen tijd is er op deze locatie geen voertuig vrij."
            : "Dit voertuig is niet beschikbaar in de gekozen periode.",
        lines: buildOccupancyLines(list, {withPlate: hasEquivalents}),
        outro: "Kies alstublieft een andere tijd.",
    };
};

export const unavailableMessageToText = (message) => (
    [message?.intro, ...(message?.lines || []), message?.outro].filter(Boolean).join("\n")
);
